// api/cart.api.js
import API_BASE from "./api";

// Session management helpers
const SESSION_KEY = 'cart_session_id';

export class CartSessionManager {
    static getSessionId() {
        let sessionId = localStorage.getItem(SESSION_KEY);
        if (!sessionId) {
            sessionId = this.generateSessionId();
            localStorage.setItem(SESSION_KEY, sessionId);
        }
        return sessionId;
    }

    static generateSessionId() {
        return 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    static clearSessionId() {
        localStorage.removeItem(SESSION_KEY);
    }

    // Add this method to CartSessionManager class
    static updateSessionId(newSessionId) {
        localStorage.setItem(SESSION_KEY, newSessionId);
    }

    static hasSession() {
        return !!localStorage.getItem(SESSION_KEY);
    }
}

// Cart event emitter for real-time updates
class CartEventEmitter {
    constructor() {
        this.listeners = new Map();
    }

    subscribe(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);

        return () => this.unsubscribe(event, callback);
    }

    unsubscribe(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }

    emit(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in ${event} listener:`, error);
                }
            });
        }
    }
}

// Global cart event emitter - ADD THIS EXPORT
export const cartEvents = new CartEventEmitter();

// Enhanced fetch wrapper with session management
// api/cart.api.js - Fix the fetchWithSession function
const fetchWithSession = async (url, options = {}) => {
    const sessionId = CartSessionManager.getSessionId();

    // Prepare headers - backend expects X-Session-ID for guest carts
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers
    };

    // Add session ID for guest users (always add it, backend will handle auth users)
    headers['X-Session-ID'] = sessionId;

    try {
        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include' // Important for cookies/sessions
        });

        const data = await response.json();

        // Handle session ID from response (for new sessions)
        if (data.sessionId && data.sessionId !== sessionId) {
            // Use let instead of const for reassignment
            CartSessionManager.updateSessionId(data.sessionId);
        }

        if (!response.ok) {
            throw new Error(data.message || `Request failed with status ${response.status}`);
        }

        return data;
    } catch (error) {
        console.error('Fetch error:', error);
        throw error;
    }
};


export class CartService {
    // Get cart with session management
    // api/cart.api.js - Update getCart method
    static async getCart() {
        try {
            const data = await fetchWithSession(`${API_BASE}/cart`, {
                method: 'GET'
            });

            // Check if response has cart data
            if (data.data && data.data.items) {
                return data.data;
            } else {
                // Return empty cart if data structure is unexpected
                console.warn('Unexpected cart response structure:', data);
                return {
                    items: [],
                    subtotal: 0,
                    discountTotal: 0,
                    finalTotal: 0,
                    appliedCartOffers: [],
                    sessionId: CartSessionManager.getSessionId()
                };
            }
        } catch (error) {
            console.error('Failed to get cart:', error);

            // Return empty cart structure if error
            return {
                items: [],
                subtotal: 0,
                discountTotal: 0,
                finalTotal: 0,
                appliedCartOffers: [],
                sessionId: CartSessionManager.getSessionId()
            };
        }
    }

    // Add item to cart - updated to match backend single item or array
    static async addToCart(itemData) {
        try {
            // Ensure proper format
            const cartItem = {
                productId: itemData.productId,
                variantId: itemData.variantId || 'default',
                quantity: Math.max(1, itemData.quantity || 1)
            };

            const data = await fetchWithSession(`${API_BASE}/cart/add`, {
                method: 'POST',
                body: JSON.stringify(cartItem) // Single item
            });

            // Emit cart update event
            cartEvents.emit('cartUpdated', data.data.cart);
            cartEvents.emit('itemAdded', {
                productId: itemData.productId,
                variantId: itemData.variantId,
                quantity: itemData.quantity || 1,
                result: data.data.results?.[0]
            });

            return {
                cart: data.data.cart,
                results: data.data.results || [],
                errors: data.data.errors || [],
                sessionId: data.sessionId,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to add to cart:', error);
            throw error;
        }
    }

    // Add multiple items to cart
    static async addMultipleToCart(items) {
        try {
            // Ensure all items have required fields
            const cartItems = Array.isArray(items) ? items : [items];
            const validatedItems = cartItems.map(item => ({
                productId: item.productId,
                variantId: item.variantId || 'default',
                quantity: Math.max(1, item.quantity || 1)
            }));

            const data = await fetchWithSession(`${API_BASE}/cart/add`, {
                method: 'POST',
                body: JSON.stringify(validatedItems) // Array of items
            });

            // Emit cart update event
            cartEvents.emit('cartUpdated', data.data.cart);
            cartEvents.emit('itemsAdded', {
                items: validatedItems,
                results: data.data.results || []
            });

            return {
                cart: data.data.cart,
                results: data.data.results || [],
                errors: data.data.errors || [],
                sessionId: data.sessionId,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to add multiple items to cart:', error);
            throw error;
        }
    }

    // Update cart item quantity
    static async updateCartItem(itemId, quantity) {
        try {
            if (quantity < 1 || quantity > 10) {
                throw new Error('Quantity must be between 1 and 10');
            }

            const data = await fetchWithSession(`${API_BASE}/cart/update`, {
                method: 'PATCH',
                body: JSON.stringify({ itemId, quantity })
            });

            // Emit cart update event
            cartEvents.emit('cartUpdated', data.data);
            cartEvents.emit('itemUpdated', { itemId, quantity });

            return {
                cart: data.data,
                sessionId: data.sessionId,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to update cart item:', error);
            throw error;
        }
    }

    // Remove item from cart
    static async removeFromCart(itemId) {
        try {
            const data = await fetchWithSession(`${API_BASE}/cart/remove/${itemId}`, {
                method: 'DELETE'
            });

            // Emit cart update event
            cartEvents.emit('cartUpdated', data.data);
            cartEvents.emit('itemRemoved', { itemId });

            return {
                cart: data.data,
                sessionId: data.sessionId,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to remove item from cart:', error);
            throw error;
        }
    }

    // Clear cart
    static async clearCart() {
        try {
            const data = await fetchWithSession(`${API_BASE}/cart/clear`, {
                method: 'DELETE'
            });

            // Emit cart update event
            cartEvents.emit('cartUpdated', data.data);
            cartEvents.emit('cartCleared');

            return {
                cart: data.data,
                sessionId: data.sessionId,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to clear cart:', error);
            throw error;
        }
    }

    // Get cart summary
    static async getCartSummary() {
        try {
            const cart = await this.getCart();

            return {
                items: cart.items || [],
                subtotal: cart.subtotal || 0,
                discountTotal: cart.discountTotal || 0,
                finalTotal: cart.finalTotal || 0,
                totalItems: cart.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0,
                sessionId: cart.sessionId,
                appliedCartOffers: cart.appliedCartOffers || []
            };
        } catch (error) {
            console.error('Failed to get cart summary:', error);
            return {
                items: [],
                subtotal: 0,
                discountTotal: 0,
                finalTotal: 0,
                totalItems: 0,
                appliedCartOffers: []
            };
        }
    }

    // Merge guest cart with user cart (call after login)
    static async mergeCart() {
        try {
            const token = localStorage.getItem('auth_token');
            // console.log(token)
            if (!token) {
                throw new Error('User must be logged in to merge cart');
            }

            const sessionId = CartSessionManager.getSessionId();
            if (!sessionId) {
                throw new Error('No guest cart to merge');
            }

            const response = await fetch(`${API_BASE}/cart/merge`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-ID': sessionId
                },
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Merge failed');
            }

            // Clear session ID after successful merge
            CartSessionManager.clearSessionId();

            // Emit cart update event
            cartEvents.emit('cartUpdated', data.data);
            cartEvents.emit('cartMerged');

            return {
                cart: data.data,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to merge cart:', error);
            throw error;
        }
    }

    // Validate cart stock
    static async validateCartStock() {
        try {
            const data = await fetchWithSession(`${API_BASE}/cart/validate-stock`, {
                method: 'POST'
            });

            return {
                success: data.success,
                isValid: data.data?.isValid || false,
                validationResults: data.data?.validationResults || [],
                insufficientItems: data.data?.insufficientItems || [],
                cart: data.data?.cart,
                message: data.message,
                sessionId: data.sessionId
            };
        } catch (error) {
            console.error('Failed to validate cart stock:', error);
            throw error;
        }
    }

    // Check if cart is empty
    static async isCartEmpty() {
        const summary = await this.getCartSummary();
        return summary.totalItems === 0;
    }

    // Get cart item count (for badge display)
    static async getCartItemCount() {
        const summary = await this.getCartSummary();
        return summary.totalItems;
    }

    // Calculate savings in cart
    static async getCartSavings() {
        const cart = await this.getCart();

        if (!cart.items || cart.items.length === 0) {
            return 0;
        }

        // Calculate total savings from discounts
        let totalSavings = 0;

        // Calculate item-level savings from original prices
        cart.items.forEach(item => {
            if (item.originalPrice && item.price) {
                const itemSavings = (item.originalPrice - item.price) * item.quantity;
                totalSavings += itemSavings;
            }
        });

        // Add cart-level discounts
        totalSavings += cart.discountTotal || 0;

        return totalSavings;
    }

    // Quick add to cart (simplified version for product cards)
    static async quickAdd(productId, variantId = 'default') {
        try {
            return await this.addToCart({
                productId,
                variantId,
                quantity: 1
            });
        } catch (error) {
            console.error('Quick add failed:', error);
            throw error;
        }
    }
}